// Ads › Online Ads — ฝัง Campaign Report ของ Biddable เฉพาะ project ที่ผูกไว้
import { useState } from "react";
import { useSearchParams } from "react-router";
import type { Brand, CampaignLink } from "@contracts/api";
import { useCampaigns } from "@features/campaigns/api/use-campaigns";
import { CampaignSelect } from "@features/campaigns/components/campaign-select";
import { EmbedHead } from "@features/embeds/components/embed-head";
import { brandCfgLink } from "@features/navigation/model/side-menus";
import { biddableUrl, type AdsScope } from "../lib/biddable-url";

/** ลิงก์เก่าที่ยังไม่มี meta = รหัส project อยู่ใน refId */
const scopeOf = (l: CampaignLink): AdsScope => ({
  project: (l.meta as AdsScope | null)?.project || l.refId,
  platforms: (l.meta as AdsScope | null)?.platforms ?? [],
});

export function OnlineAdsPage({ brand }: { brand: Brand }) {
  const [params] = useSearchParams();
  const { data: campaigns, isPending } = useCampaigns(brand.id);
  const list = campaigns ?? [];
  const campaign = list.find((c) => c.id === params.get("campaign"));
  // ผูกกับ project ฝั่ง Biddable — ลิงก์ ads/ads_tiktok แบบเก่าชี้ไปหน้าที่เขาลบทิ้งแล้ว
  const adsLinks = (campaign?.links ?? []).filter((l) => l.kind === "ads_project");

  // ผูกได้หลาย project ต่อหนึ่งแคมเปญของเรา → มีมากกว่า 1 ให้เลือกจาก dropdown
  const wantRef = params.get("ref");
  const [pickedIdx, setPickedIdx] = useState<number | null>(null);
  const fromUrl = adsLinks.findIndex((l) => l.refId === wantRef);
  const idx = pickedIdx ?? (fromUrl >= 0 ? fromUrl : 0);
  const current = adsLinks[idx];

  return (
    <>
      <EmbedHead
        crumb={`${brand.name} / Ads / Online Ads`}
        title="Online Ads"
        right={
          <>
            <CampaignSelect campaigns={list} />
            {adsLinks.length > 1 && (
              <label className="camp-select-wrap">
                <span className="muted camp-select-lbl">Project</span>
                <select
                  className="camp-select"
                  value={idx}
                  onChange={(e) => setPickedIdx(Number(e.target.value))}
                >
                  {adsLinks.map((l, i) => (
                    <option key={l.id} value={i}>
                      {l.refName || l.refId}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </>
        }
        note={
          campaign && current ? (
            // ชื่อบัญชี/แคมเปญฝั่ง Biddable ไม่ต้องเขียนซ้ำที่นี่ — กรอบข้างล่างพิมพ์ scope
            // ของตัวเองอยู่แล้วใต้หัวเรื่อง · เหลือไว้เฉพาะแคมเปญของเรา ซึ่งเขาไม่รู้จัก
            <>
              แคมเปญ: {campaign.name} ·{" "}
              <a
                href={biddableUrl(scopeOf(current), { embed: false })}
                target="_blank"
                rel="noopener"
              >
                เปิดในแท็บใหม่ ↗
              </a>
            </>
          ) : undefined
        }
      />
      <div className="embed-frame">
        {isPending ? (
          <div className="embed-empty">
            <p className="muted">กำลังโหลดแคมเปญ…</p>
          </div>
        ) : !campaign ? (
          <div className="embed-empty">
            <h3>เลือกแคมเปญก่อน</h3>
            <p className="muted">
              {list.length ? (
                "รายงานโฆษณาผูกกับแคมเปญ — เลือกแคมเปญจาก dropdown ด้านบน"
              ) : (
                <>
                  แบรนด์นี้ยังไม่มีแคมเปญ — สร้างได้ที่{" "}
                  <a href={brandCfgLink(brand)}>หน้าตั้งค่า → Campaigns</a>
                </>
              )}
            </p>
          </div>
        ) : !current ? (
          <div className="embed-empty">
            <h3>ยังไม่ได้ผูก project</h3>
            <p className="muted">
              แคมเปญ &quot;{campaign.name}&quot; ยังไม่ได้เลือกงานฝั่ง Biddable
              <br />
              เลือก project ได้ที่{" "}
              <a href={brandCfgLink(brand)}>หน้าตั้งค่า → Campaigns → Online Ads</a>
            </p>
          </div>
        ) : (
          <iframe
            key={current.id}
            src={biddableUrl(scopeOf(current))}
            title="Online Ads"
            referrerPolicy="no-referrer"
          />
        )}
      </div>
    </>
  );
}
